export interface PlayerBridgeProgressInput {
  type: string;
  status: string;
  bridge?: {
    acknowledgedAt?: string | null;
  };
}

export interface PlayerBridgeProgress {
  label: string;
  state: "queued" | "working" | "complete" | "attention";
  message: string;
}

export function playerBridgeOperationLabel(type: string): string {
  switch (type) {
    case "garage_park_live_dino": return "Parking";
    case "garage_redeem_stored_dino": return "Retrieval";
    case "corpse_drop.request": return "Food request";
    case "prime_upgrade_live_dino": return "Prime upgrade";
    case "skin_apply_live_dino": return "Skin application";
    case "mutation_inspect_dino": return "Mutation slot inspection";
    case "mutation_correct_dino": return "Mutation correction";
    case "player_stats_observe": return "Live dinosaur detection";
    case "slay_live_dino": return "Slay";
    case "ai_spawn.request": return "AI spawn";
    default: return "Game action";
  }
}

export function formatPlayerBridgeProgress(
  operation: PlayerBridgeProgressInput,
  label = playerBridgeOperationLabel(operation.type)
): PlayerBridgeProgress {
  if (operation.status === "confirmed") {
    const message = confirmedMessage(operation.type, label);
    return { label, state: "complete", message };
  }

  if (operation.status === "manual_review") {
    return {
      label,
      state: "attention",
      message: `${label} could not be automatically verified. Staff review is required before you retry.`
    };
  }

  if (operation.status === "failed") {
    return { label, state: "attention", message: failedMessage(operation.type, label) };
  }

  if (operation.status === "denied") {
    return { label, state: "attention", message: `${label} was declined. Nothing was changed.` };
  }

  if (operation.status === "refunded") {
    return { label, state: "attention", message: `${label} could not be completed. Any charged points were refunded.` };
  }

  if (operation.status === "sent") {
    const received = Boolean(operation.bridge?.acknowledgedAt);
    return {
      label,
      state: "working",
      message: received
        ? `${label} reached the game server and is waiting for final confirmation.${pendingGuidance(operation.type)}`
        : `${label} was sent to the game server and is waiting to be processed.${pendingGuidance(operation.type)}`
    };
  }

  return {
    label,
    state: "queued",
    message: `${label} was accepted and is waiting to be sent to the game server.${pendingGuidance(operation.type)}`
  };
}

function failedMessage(type: string, label: string): string {
  switch (type) {
    case "mutation_correct_dino":
      return "Mutation correction failed. Refresh the exact mutation slots before trying again.";
    case "mutation_inspect_dino":
      return "Mutation slot inspection failed. Keep the selected dinosaur available and load its exact slots again.";
    default:
      return `${label} failed. Nothing was confirmed as changed.`;
  }
}

function confirmedMessage(type: string, label: string): string {
  switch (type) {
    case "garage_park_live_dino":
      return "Parking complete. Your dinosaur is safe in the garage.";
    case "garage_redeem_stored_dino":
      return "Retrieval complete. The saved dinosaur, vitals, and parked location were restored.";
    case "corpse_drop.request":
      return "Food request complete. The corpse spawned near your current position.";
    case "prime_upgrade_live_dino":
      return "Prime upgrade complete. Prime is active on your live dinosaur.";
    case "skin_apply_live_dino":
      return "Skin application complete. The colours were verified and saved for restoration.";
    case "player_stats_observe":
      return "Live dinosaur detection complete. Fresh telemetry will appear on the next game update.";
    case "slay_live_dino":
      return "Slay complete. The selected live dinosaur was permanently slain and was not added to the garage.";
    case "ai_spawn.request":
      return "AI spawn complete.";
    default:
      return `${label} complete.`;
  }
}

function pendingGuidance(type: string): string {
  switch (type) {
    case "garage_park_live_dino":
      return " Stay connected until confirmation appears.";
    case "garage_redeem_stored_dino":
      return " Stay in-game on the matching species.";
    case "corpse_drop.request":
      return " Stay near your current position.";
    case "skin_apply_live_dino":
    case "prime_upgrade_live_dino":
      return " Keep the same live dinosaur active.";
    case "player_stats_observe":
      return " Stay spawned in-game while your linked Steam account is checked.";
    case "slay_live_dino":
      return " Keep the selected live dinosaur active until confirmation appears.";
    default:
      return "";
  }
}
